import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Compass, Layers, Sparkles, Target } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { LANDING } from "@/constants/testIds";

const STEPS = [
  {
    icon: Compass,
    title: "Answer 5 quick questions",
    body: "Tell us about your business, budget and the outcome you want next.",
  },
  {
    icon: Sparkles,
    title: "Get an AI-personalized read",
    body: "Claude writes a short, tailored note on why this stack fits you.",
  },
  {
    icon: Layers,
    title: "Start with 3 hand-picked tools",
    body: "Curated from the most-used AI tools that operators actually pay for.",
  },
];

const CATEGORIES = [
  "AI Writing",
  "Automation",
  "Customer Support",
  "SEO",
  "Productivity",
  "No-Code",
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-in-up">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0f766e] bg-[#ccfbf1] px-3 py-1 rounded-full">
                <Target className="w-3.5 h-3.5" />
                AI Tool Finder
              </span>
              <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                Stop guessing.{" "}
                <span className="text-[#0f766e]">Find the 3 AI tools</span>
                <br className="hidden sm:block" /> built for your business.
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                Answer 5 short questions. We'll match you with the right AI tools across
                writing, automation, customer support, SEO, productivity and no-code —
                plus a personalized note on why they fit.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid={LANDING.startQuizBtn}
                  onClick={() => navigate("/quiz")}
                  className="bg-[#0f766e] hover:bg-[#115e59] text-white h-12 px-7 text-base"
                >
                  Start the 60-second quiz
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    const el = document.getElementById("how-it-works");
                    el?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="h-12 px-6 text-slate-700 hover:bg-slate-100"
                >
                  How it works
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <span
                    key={c}
                    className="text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-full"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 fade-in-up" style={{ animationDelay: "120ms" }}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#ccfbf1] to-transparent rounded-3xl blur-2xl opacity-60" />
                <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-xs text-slate-500 font-medium">Sample recommendation</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#0f766e]">Live</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    “For a solo founder focused on growing revenue with a $50–$200
                    budget, start with <span className="text-slate-900 font-medium">ChatGPT</span> for
                    drafts, <span className="text-slate-900 font-medium">Zapier</span> to glue your stack
                    together, and <span className="text-slate-900 font-medium">Perplexity</span> for research.”
                  </p>
                  <div className="mt-5 grid grid-cols-3 gap-2">
                    {["ChatGPT", "Zapier", "Perplexity"].map((t) => (
                      <div
                        key={t}
                        className="border border-slate-200 rounded-md py-3 text-center text-xs font-medium text-slate-700"
                      >
                        {t}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0f766e]">
              How it works
            </span>
            <h2 className="font-display mt-3 text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              Three steps. Real recommendations.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              No filler. No 50-tool comparison tables. Just a focused short-list that
              matches your size, skill and budget.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div
                key={s.title}
                className="bg-white border border-slate-200 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-[#0f766e]"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-md bg-[#ccfbf1] text-[#0f766e] grid place-items-center">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-bold text-slate-400">0{i + 1}</span>
                </div>
                <h3 className="font-display mt-5 text-lg font-semibold text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
            Ready to find your stack?
          </h2>
          <p className="mt-4 text-slate-600">
            It takes about a minute. No account needed to start.
          </p>
          <Button
            onClick={() => navigate("/quiz")}
            className="mt-8 bg-[#0f766e] hover:bg-[#115e59] text-white h-12 px-8 text-base"
            data-testid="cta-bottom-start-quiz"
          >
            Start the quiz
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
